import React, { useState, useEffect } from "react";
import { X, Save, RefreshCcw, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { adminPlanService } from "../../../../services/api/settings";
import { ServiceType } from "../../../../store/types/settings";

interface EditPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any;
  onSuccess: () => void;
}

const EditPlanModal: React.FC<EditPlanModalProps> = ({
  isOpen,
  onClose,
  data,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [newPoint, setNewPoint] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    description: "",
    points: [] as string[],
    serviceType: "CONSOLE" as ServiceType,
    tier: "",
    monthlyPrice: 0,
    quarterlyPrice: 0,
    yearlyPrice: 0,
    setupFee: 0,
    integrationCost: 0,
    deploymentCosts: [] as {
      name: string;
      description: string;
      price: number;
    }[],
    users: "",
    isHighlighted: false,
    syncPrice: true,
  });

  useEffect(() => {
    if (data) {
      setFormData({
        title: data.title || data.name || "",
        subtitle: data.subtitle || "",
        description: data.description || "",
        points: Array.isArray(data.points)
          ? [...data.points]
          : Array.isArray(data.features)
            ? [...data.features]
            : [],
        serviceType: data.serviceType || "CONSOLE",
        tier: data.tier || "",
        monthlyPrice: parseFloat(data.monthlyPrice || "0"),
        quarterlyPrice: parseFloat(data.quarterlyPrice || "0"),
        yearlyPrice: parseFloat(data.yearlyPrice || "0"),
        setupFee: parseFloat(data.setupFee || "0"),
        integrationCost: parseFloat(data.integrationCost || "0"),
        deploymentCosts: data.costBreakdown?.deploymentCosts
          ? JSON.parse(JSON.stringify(data.costBreakdown.deploymentCosts)) // Deep copy
          : [],
        users: data.users || "",
        isHighlighted: data.isHighlighted || false,
        syncPrice: false,
      });
    }
  }, [data]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload: any = {
        ...formData,
        features: formData.points, // Explicitly mapping points back to features for the API if needed
        syncPrice: true,
      };

      // Clean up tier if console
      if (formData.serviceType === "CONSOLE") {
        payload.tier = null;
      }

      // Cost Breakdown
      if (formData.deploymentCosts.length > 0) {
        payload.costBreakdown = {
          deploymentCosts: formData.deploymentCosts,
        };
      } else {
        // Explicitly clear it if empty depending on backend preferences
        payload.costBreakdown = null;
      }
      delete payload.deploymentCosts;

      // Convert prices back to kobo/millia (multiply by 1000)
      payload.monthlyPrice = formData.monthlyPrice.toString();
      payload.quarterlyPrice = Number(formData.quarterlyPrice);
      payload.yearlyPrice = formData.yearlyPrice.toString();

      await adminPlanService.update(data.id, payload);
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : type === "number"
            ? Number(value)
            : value,
    }));
  };

  const addPoint = () => {
    if (newPoint.trim()) {
      setFormData((prev) => ({
        ...prev,
        points: [...prev.points, newPoint.trim()],
      }));
      setNewPoint("");
    }
  };

  const removePoint = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      points: prev.points.filter((_, i) => i !== index),
    }));
  };

  const addDeploymentCost = () => {
    setFormData((prev) => ({
      ...prev,
      deploymentCosts: [
        ...prev.deploymentCosts,
        { name: "", description: "", price: 0 },
      ],
    }));
  };

  const updateDeploymentCost = (
    index: number,
    field: string,
    value: string | number,
  ) => {
    setFormData((prev) => {
      const newCosts = [...prev.deploymentCosts];
      newCosts[index] = { ...newCosts[index], [field]: value };
      return { ...prev, deploymentCosts: newCosts };
    });
  };

  const removeDeploymentCost = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      deploymentCosts: prev.deploymentCosts.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-3xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh] font-abel">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Edit Plan</h2>
            <p className="text-sm text-gray-500 font-medium">
              Update details for {data.title || data.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all"
          >
            <X size={24} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="overflow-y-auto flex-1 h-full flex flex-col"
        >
          <div className="p-6 space-y-6 flex-1">
            <div className="space-y-4 animate-in fade-in duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 leading-none">
                    Plan Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 leading-none">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    name="subtitle"
                    value={formData.subtitle}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 leading-none">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none resize-none"
                  required
                />
              </div>

              {formData.serviceType !== "CONSOLE" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 leading-none">
                    Tier Assignment
                  </label>
                  <select
                    name="tier"
                    value={formData.tier}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                    required
                  >
                    {formData.serviceType === "RECRUITER" ? (
                      <>
                        <option value="STARTER">STARTER</option>
                        <option value="GROWTH">GROWTH</option>
                        <option value="SCALE">SCALE</option>
                        <option value="ENTERPRISE">ENTERPRISE</option>
                      </>
                    ) : (
                      <>
                        <option value="STARTER">STARTER</option>
                        <option value="GROWTH">GROWTH</option>
                        <option value="SCALE">SCALE</option>
                        <option value="ENTERPRISE">ENTERPRISE</option>
                      </>
                    )}
                  </select>
                </div>
              )}

              {/* Edit Prices Toggle */}
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="syncPrice"
                  name="syncPrice"
                  checked={formData.syncPrice}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2 cursor-pointer"
                />
                <label
                  htmlFor="syncPrice"
                  className="text-sm font-medium text-gray-700 cursor-pointer"
                >
                  Edit Prices
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 leading-none">
                    Monthly Price
                  </label>
                  <input
                    type="number"
                    name="monthlyPrice"
                    value={formData.monthlyPrice}
                    onChange={handleInputChange}
                    disabled={!formData.syncPrice}
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                    required
                    min={0}
                  />
                  <p className="text-[10px] text-gray-400 mt-1.5 font-bold">
                    ₦{formData.monthlyPrice.toLocaleString()} Equivalent
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 leading-none">
                    Quarterly Price
                  </label>
                  <input
                    type="number"
                    name="quarterlyPrice"
                    value={formData.quarterlyPrice}
                    onChange={handleInputChange}
                    disabled={!formData.syncPrice}
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                    required
                    min={0}
                  />
                  <p className="text-[10px] text-gray-400 mt-1.5 font-bold">
                    ₦{formData.quarterlyPrice.toLocaleString()} Equivalent
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 leading-none">
                    Yearly Price
                  </label>
                  <input
                    type="number"
                    name="yearlyPrice"
                    value={formData.yearlyPrice}
                    onChange={handleInputChange}
                    disabled={!formData.syncPrice}
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                    required
                    min={0}
                  />
                  <p className="text-[10px] text-gray-400 mt-1.5 font-bold">
                    ₦{formData.yearlyPrice.toLocaleString()} Equivalent
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 leading-none">
                    Setup Fee
                  </label>
                  <input
                    type="number"
                    name="setupFee"
                    value={formData.setupFee}
                    onChange={handleInputChange}
                    disabled={!formData.syncPrice}
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                    min={0}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 leading-none">
                    Integration Cost
                  </label>
                  <input
                    type="number"
                    name="integrationCost"
                    value={formData.integrationCost}
                    onChange={handleInputChange}
                    disabled={!formData.syncPrice}
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                    min={0}
                  />
                </div>
              </div>

              {formData.serviceType === "CONSOLE" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 leading-none">
                    Users Limit Label
                  </label>
                  <input
                    type="text"
                    name="users"
                    value={formData.users}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                    placeholder="Unlimited users"
                  />
                </div>
              )}

              {/* Points Manager */}
              <div className="pt-4 pb-2 border-t border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Plan Points / Features
                  </label>
                  <span className="text-xs font-bold text-primary bg-primary/5 px-2 py-0.5 rounded-full">
                    {formData.points.length} Points
                  </span>
                </div>

                <div className="space-y-2">
                  {formData.points.map((point, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 group animate-in slide-in-from-left-2 duration-200"
                    >
                      <div className="flex-1 flex items-center gap-3 px-4 py-3 bg-gray-50 border border-transparent rounded-lg">
                        <CheckCircle2 size={16} className="text-emerald-500" />
                        <span className="flex-1 text-sm font-medium text-gray-700">
                          {point}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePoint(index)}
                        className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}

                  <div className="flex items-center gap-2 pt-2">
                    <input
                      type="text"
                      placeholder="Add a new point..."
                      value={newPoint}
                      onChange={(e) => setNewPoint(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addPoint();
                        }
                      }}
                      className="flex-1 px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                    />
                    <button
                      type="button"
                      onClick={addPoint}
                      disabled={!newPoint.trim()}
                      className="p-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all disabled:opacity-50 disabled:bg-gray-300"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Deployment Costs Manager */}
              <div className="pt-4 pb-2 border-t border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Deployment Costs Breakdown
                  </label>
                  <span className="text-xs font-bold text-primary bg-primary/5 px-2 py-0.5 rounded-full">
                    {formData.deploymentCosts.length} Items
                  </span>
                </div>

                <div className="space-y-4">
                  {formData.deploymentCosts.map((cost, index) => (
                    <div
                      key={index}
                      className="p-4 bg-gray-50 border border-gray-200 rounded-lg relative group"
                    >
                      <button
                        type="button"
                        onClick={() => removeDeploymentCost(index)}
                        className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-all opacity-0 group-hover:opacity-100 bg-white"
                        title="Remove Item"
                      >
                        <Trash2 size={16} />
                      </button>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3 pr-8">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Name
                          </label>
                          <input
                            type="text"
                            value={cost.name}
                            onChange={(e) =>
                              updateDeploymentCost(
                                index,
                                "name",
                                e.target.value,
                              )
                            }
                            placeholder="e.g. Server Setup"
                            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Price
                          </label>
                          <input
                            type="number"
                            value={cost.price}
                            onChange={(e) =>
                              updateDeploymentCost(
                                index,
                                "price",
                                Number(e.target.value),
                              )
                            }
                            placeholder="0"
                            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                            required
                            min={0}
                          />
                        </div>
                      </div>
                      {/* <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Description
                        </label>
                        <textarea
                          value={cost.description}
                          onChange={(e) => updateDeploymentCost(index, "description", e.target.value)}
                          placeholder="e.g. Initial server deployment and configuration"
                          rows={2}
                          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                          required
                        />
                      </div> */}
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addDeploymentCost}
                    className="w-full py-3 flex items-center justify-center gap-2 bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-primary hover:border-primary/30 transition-all text-sm font-medium"
                  >
                    <Plus size={18} />
                    Add Deployment Cost Item
                  </button>
                </div>
              </div>

              <div className="flex gap-6 pt-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="isHighlighted"
                    checked={formData.isHighlighted}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-primary rounded border-gray-300 focus:ring-primary"
                  />
                  <span className="text-gray-700 text-sm font-medium">
                    Highlight Plan (Popular Tag)
                  </span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="syncPrice"
                    checked={formData.syncPrice}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-primary rounded border-gray-300 focus:ring-primary"
                  />
                  <span className="text-gray-700 text-sm font-medium">
                    Sync Pricing with UI
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-gray-100 flex items-center justify-between bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 text-gray-600 font-bold hover:bg-gray-100 rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-8 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all shadow-md shadow-primary/20 font-bold disabled:opacity-50"
            >
              {loading ? (
                <RefreshCcw size={20} className="animate-spin" />
              ) : (
                <Save size={20} />
              )}
              <span>{loading ? "Saving..." : "Save Changes"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPlanModal;
